import React from 'react';
import { createPortal } from 'react-dom';
import { HealthcareProvider, ProviderLicenseStatus } from '../interfaces';
import { Theme } from '../Theme';

export type HealthcareProviderTreeNode = HealthcareProviderFolderNode | HealthcareProviderLeafNode;

export type HealthcareProviderFolderNode = {
    type: 'folder';
    id: string;
    name: string;
    children: Array<HealthcareProviderTreeNode>;
};

export type HealthcareProviderLeafNode = {
    type: 'provider';
    id: string;
    provider: HealthcareProvider;
};

type MutableHealthcareProviderFolder = {
    name: string;
    path: string;
    folders: Map<string, MutableHealthcareProviderFolder>;
    providers: Array<HealthcareProvider>;
};

type HealthcareProviderSelectorProps = {
    healthcareProviderId: string;
    healthcareProviders: Array<HealthcareProvider>;
    clientCountByProviderId: Record<string, number>;
    licenseStatusByProviderId?: Record<string, ProviderLicenseStatus | undefined>;
    onChange: (healthcareProviderId: string) => void;
    onOpen?: () => void;
    style?: React.CSSProperties;
};

type MenuPosition = {
    top: number;
    right: number;
    width: number;
    maxHeight: number;
};

const MENU_OFFSET_PX = 6;
const MENU_MIN_WIDTH_PX = 560;
const MENU_MAX_WIDTH_PX = 720;
const MENU_MAX_HEIGHT_PX = 620;
const VIEWPORT_MARGIN_PX = 8;

// Keep folder and provider ordering predictable as the provider list changes.
function compareLabels(leftLabel: string, rightLabel: string): number {
    return leftLabel.localeCompare(rightLabel, undefined, {
        numeric: true,
        sensitivity: 'base',
    });
}

// Convert a mutable folder map into the recursive shape rendered by the selector.
function finaliseHealthcareProviderFolder(
    folder: MutableHealthcareProviderFolder
): Array<HealthcareProviderTreeNode> {
    const childFolders: Array<HealthcareProviderFolderNode> = Array.from(folder.folders.values())
        .sort((leftFolder, rightFolder) => compareLabels(leftFolder.name, rightFolder.name))
        .map((childFolder) => ({
            type: 'folder',
            id: childFolder.path,
            name: childFolder.name,
            children: finaliseHealthcareProviderFolder(childFolder),
        }));
    const childProviders: Array<HealthcareProviderLeafNode> = [...folder.providers]
        .sort((leftProvider, rightProvider) =>
            compareLabels(leftProvider.name, rightProvider.name) ||
            compareLabels(leftProvider.id, rightProvider.id)
        )
        .map((provider) => ({
            type: 'provider',
            id: provider.id,
            provider,
        }));

    // Folders come first, matching familiar file-browser ordering.
    return [...childFolders, ...childProviders];
}

// Derive a folder tree from each provider's explicit slash-separated path.
export function buildHealthcareProviderTree(
    healthcareProviders: Array<HealthcareProvider>
): Array<HealthcareProviderTreeNode> {
    const root: MutableHealthcareProviderFolder = {
        name: '',
        path: '',
        folders: new Map(),
        providers: [],
    };

    healthcareProviders.forEach((provider) => {
        // Ignore empty segments so malformed paths never create blank folder rows.
        const providerPath = typeof provider.path === 'string' ? provider.path : '';
        const folderNames = providerPath
            .split('/')
            .map((segment) => segment.trim())
            .filter((segment) => segment.length > 0);
        let parentFolder = root;

        folderNames.forEach((folderName) => {
            const folderPath = parentFolder.path ? `${parentFolder.path}/${folderName}` : folderName;
            let childFolder = parentFolder.folders.get(folderName);
            if (!childFolder) {
                childFolder = {
                    name: folderName,
                    path: folderPath,
                    folders: new Map(),
                    providers: [],
                };
                parentFolder.folders.set(folderName, childFolder);
            }
            parentFolder = childFolder;
        });

        parentFolder.providers.push(provider);
    });

    return finaliseHealthcareProviderFolder(root);
}

// Return every ancestor path needed to reveal a selected provider in the tree.
export function getHealthcareProviderFolderPaths(healthcareProviderPath?: string): Array<string> {
    const safeHealthcareProviderPath = typeof healthcareProviderPath === 'string' ? healthcareProviderPath : '';
    const pathSegments = safeHealthcareProviderPath
        .split('/')
        .map((segment) => segment.trim())
        .filter((segment) => segment.length > 0);

    return pathSegments.map((_, segmentIndex) =>
        pathSegments.slice(0, segmentIndex + 1).join('/')
    );
}

// Prefix every provider with a count, including providers with no registered people.
export function getHealthcareProviderLabel(
    provider: HealthcareProvider,
    clientCountByProviderId: Record<string, number>,
    licenseStatusByProviderId: Record<string, ProviderLicenseStatus | undefined> = {}
): string {
    const clientCount = clientCountByProviderId[provider.id] ?? 0;
    const warningPrefix = licenseStatusByProviderId[provider.id] === 'overage' ? '⚠️ ' : '';
    return `(${clientCount}) ${warningPrefix}${provider.name}`;
}

// Render the provider selector as a portal-backed tree so nested folders are not clipped by the dashboard.
export function HealthcareProviderSelector(props: HealthcareProviderSelectorProps) {
    const {
        healthcareProviderId,
        healthcareProviders,
        clientCountByProviderId,
        licenseStatusByProviderId = {},
        onChange,
        onOpen,
        style,
    } = props;
    const selectedProvider = healthcareProviders.find((provider) => provider.id === healthcareProviderId);
    const selectedFolderPaths = React.useMemo(
        () => getHealthcareProviderFolderPaths(selectedProvider?.path),
        [selectedProvider?.path]
    );
    const providerTree = React.useMemo(
        () => buildHealthcareProviderTree(healthcareProviders),
        [healthcareProviders]
    );
    const [isOpen, setIsOpen] = React.useState(false);
    const [expandedFolderPaths, setExpandedFolderPaths] = React.useState<Set<string>>(
        () => new Set(selectedFolderPaths)
    );
    const [hoveredItemId, setHoveredItemId] = React.useState<string | null>(null);
    const [focusedItemId, setFocusedItemId] = React.useState<string | null>(null);
    const [menuPosition, setMenuPosition] = React.useState<MenuPosition>({
        top: 0,
        right: VIEWPORT_MARGIN_PX,
        width: MENU_MIN_WIDTH_PX,
        maxHeight: MENU_MAX_HEIGHT_PX,
    });
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);
    const menuRef = React.useRef<HTMLDivElement | null>(null);
    const treeItemRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
    const typeaheadBufferRef = React.useRef('');
    const typeaheadResetTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
        // Always expand the selected provider's ancestors when selection changes externally.
        setExpandedFolderPaths((currentPaths) => {
            const nextPaths = new Set(currentPaths);
            selectedFolderPaths.forEach((folderPath) => nextPaths.add(folderPath));
            return nextPaths;
        });
    }, [selectedFolderPaths]);

    const updateMenuPosition = React.useCallback(() => {
        // Clamp the popup to the viewport and open above the trigger when space below is tight.
        const buttonRect = buttonRef.current?.getBoundingClientRect();
        if (!buttonRect) {
            return;
        }

        const viewportWidth = Math.max(window.innerWidth, (VIEWPORT_MARGIN_PX * 2) + 1);
        const viewportHeight = Math.max(window.innerHeight, 240);
        const availableWidth = viewportWidth - (VIEWPORT_MARGIN_PX * 2);
        const width = Math.min(
            Math.max(buttonRect.width, MENU_MIN_WIDTH_PX),
            Math.min(MENU_MAX_WIDTH_PX, availableWidth)
        );
        const right = Math.min(
            Math.max(VIEWPORT_MARGIN_PX, viewportWidth - buttonRect.right),
            Math.max(VIEWPORT_MARGIN_PX, viewportWidth - width - VIEWPORT_MARGIN_PX)
        );
        const spaceBelow = viewportHeight - buttonRect.bottom - MENU_OFFSET_PX - VIEWPORT_MARGIN_PX;
        const spaceAbove = buttonRect.top - MENU_OFFSET_PX - VIEWPORT_MARGIN_PX;
        const shouldOpenAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
        const maxHeight = Math.min(
            MENU_MAX_HEIGHT_PX,
            Math.max(120, shouldOpenAbove ? spaceAbove : spaceBelow)
        );
        const top = shouldOpenAbove
            ? Math.max(VIEWPORT_MARGIN_PX, buttonRect.top - MENU_OFFSET_PX - maxHeight)
            : buttonRect.bottom + MENU_OFFSET_PX;

        setMenuPosition({ top, right, width, maxHeight });
    }, []);

    const closeMenu = React.useCallback((returnFocus = false) => {
        setIsOpen(false);
        setHoveredItemId(null);
        setFocusedItemId(null);
        typeaheadBufferRef.current = '';
        if (typeaheadResetTimerRef.current) {
            clearTimeout(typeaheadResetTimerRef.current);
            typeaheadResetTimerRef.current = null;
        }
        if (returnFocus) {
            window.requestAnimationFrame(() => buttonRef.current?.focus());
        }
    }, []);

    React.useEffect(() => () => {
        // Release the short-lived typeahead timer when the selector unmounts.
        if (typeaheadResetTimerRef.current) {
            clearTimeout(typeaheadResetTimerRef.current);
        }
    }, []);

    const openMenu = () => {
        // Re-open every selected ancestor so the current provider is always visible.
        onOpen?.();
        setExpandedFolderPaths((currentPaths) => {
            const nextPaths = new Set(currentPaths);
            selectedFolderPaths.forEach((folderPath) => nextPaths.add(folderPath));
            return nextPaths;
        });
        updateMenuPosition();
        setIsOpen(true);
    };

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        // Close on outside clicks or Escape, matching the other dashboard pull-down menus.
        const handleDocumentMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (!containerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
                closeMenu();
            }
        };
        const handleDocumentKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeMenu(true);
            }
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);
        document.addEventListener('keydown', handleDocumentKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
            document.removeEventListener('keydown', handleDocumentKeyDown);
        };
    }, [closeMenu, isOpen]);

    React.useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }

        // Keep the popup attached to the trigger while the dashboard scrolls or resizes.
        updateMenuPosition();
        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);
        return () => {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [isOpen, updateMenuPosition]);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        // Put keyboard focus on the selected provider, or the first visible row.
        const animationFrame = window.requestAnimationFrame(() => {
            const selectedItem = menuRef.current?.querySelector<HTMLDivElement>('[aria-current="true"]');
            const firstItem = menuRef.current?.querySelector<HTMLDivElement>('[role="treeitem"]');
            const itemToFocus = selectedItem || firstItem;
            if (itemToFocus) {
                setFocusedItemId(itemToFocus.dataset.treeItemId || null);
                itemToFocus.focus();
            }
        });
        return () => window.cancelAnimationFrame(animationFrame);
    }, [isOpen]);

    const toggleFolder = (folderPath: string) => {
        // Expand or collapse only the requested branch.
        setExpandedFolderPaths((currentPaths) => {
            const nextPaths = new Set(currentPaths);
            if (nextPaths.has(folderPath)) {
                nextPaths.delete(folderPath);
            } else {
                nextPaths.add(folderPath);
            }
            return nextPaths;
        });
    };

    const focusTreeItem = (itemId: string) => {
        // Keep one active tree row in the tab order while arrow keys move focus.
        setFocusedItemId(itemId);
        treeItemRefs.current.get(itemId)?.focus();
    };

    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.defaultPrevented) {
            return;
        }

        if (event.key === 'Tab') {
            // Restore the trigger before the browser continues normal tab navigation.
            closeMenu();
            buttonRef.current?.focus();
            return;
        }

        const visibleItems = Array.from(
            menuRef.current?.querySelectorAll<HTMLDivElement>('[role="treeitem"]') || []
        );
        if (visibleItems.length === 0) {
            return;
        }

        if (
            event.key.length === 1 &&
            event.key.trim().length === 1 &&
            !event.altKey &&
            !event.ctrlKey &&
            !event.metaKey
        ) {
            // Match typed characters against visible folder and provider names.
            event.preventDefault();
            const typedCharacter = event.key.toLocaleLowerCase();
            let query = `${typeaheadBufferRef.current}${typedCharacter}`;
            const currentIndex = visibleItems.findIndex((item) => item === document.activeElement);
            const orderedItems = [
                ...visibleItems.slice(currentIndex + 1),
                ...visibleItems.slice(0, currentIndex + 1),
            ];
            let matchingItem = orderedItems.find((item) =>
                (item.dataset.treeLabel || '').toLocaleLowerCase().startsWith(query)
            );

            // Repeated first characters cycle through matches instead of getting stuck.
            if (!matchingItem && query.length > 1) {
                query = typedCharacter;
                matchingItem = orderedItems.find((item) =>
                    (item.dataset.treeLabel || '').toLocaleLowerCase().startsWith(query)
                );
            }
            typeaheadBufferRef.current = query;
            if (typeaheadResetTimerRef.current) {
                clearTimeout(typeaheadResetTimerRef.current);
            }
            typeaheadResetTimerRef.current = setTimeout(() => {
                typeaheadBufferRef.current = '';
                typeaheadResetTimerRef.current = null;
            }, 600);

            const matchingItemId = matchingItem?.dataset.treeItemId;
            if (matchingItemId) {
                focusTreeItem(matchingItemId);
            }
            return;
        }

        // Arrow keys move through the currently visible folder and provider rows.
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            return;
        }

        event.preventDefault();
        const currentIndex = visibleItems.findIndex((item) => item === document.activeElement);
        let nextIndex = Math.max(0, currentIndex);
        if (event.key === 'End') {
            nextIndex = visibleItems.length - 1;
        } else if (event.key === 'Home') {
            nextIndex = 0;
        } else if (event.key === 'ArrowUp') {
            nextIndex = Math.max(0, currentIndex - 1);
        } else if (event.key === 'ArrowDown') {
            nextIndex = Math.min(visibleItems.length - 1, currentIndex + 1);
        }
        const nextItemId = visibleItems[nextIndex].dataset.treeItemId;
        if (nextItemId) {
            focusTreeItem(nextItemId);
        }
    };

    const renderTreeNodes = (
        nodes: Array<HealthcareProviderTreeNode>,
        depth = 0,
        parentItemId?: string
    ): React.ReactNode => nodes.map((node) => {
        if (node.type === 'folder') {
            const isExpanded = expandedFolderPaths.has(node.id);
            const itemId = `folder:${node.id}`;
            const firstChildItemId = node.children[0]
                ? `${node.children[0].type}:${node.children[0].id}`
                : undefined;
            return (
                <div
                    key={itemId}
                    ref={(element) => {
                        if (element) {
                            treeItemRefs.current.set(itemId, element);
                        } else {
                            treeItemRefs.current.delete(itemId);
                        }
                    }}
                    role='treeitem'
                    data-tree-item-id={itemId}
                    data-tree-label={node.name}
                    tabIndex={focusedItemId === itemId ? 0 : -1}
                    aria-label={node.name}
                    aria-expanded={isExpanded}
                    aria-selected={false}
                    aria-level={depth + 1}
                    onFocus={(event) => {
                        if (event.target === event.currentTarget) {
                            setFocusedItemId(itemId);
                        }
                    }}
                    onClick={(event) => {
                        // Ignore clicks from nested descendants; only this folder row toggles the branch.
                        const folderRow = event.currentTarget.firstElementChild;
                        const clickTarget = event.target as Node;
                        if (event.target !== event.currentTarget && !folderRow?.contains(clickTarget)) {
                            return;
                        }
                        focusTreeItem(itemId);
                        toggleFolder(node.id);
                    }}
                    onKeyDown={(event) => {
                        // Parent tree items ignore keyboard events bubbling from their descendants.
                        if (event.target !== event.currentTarget) {
                            return;
                        }

                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleFolder(node.id);
                        } else if (event.key === 'ArrowRight') {
                            event.preventDefault();
                            if (!isExpanded) {
                                toggleFolder(node.id);
                            } else if (firstChildItemId) {
                                focusTreeItem(firstChildItemId);
                            }
                        } else if (event.key === 'ArrowLeft') {
                            event.preventDefault();
                            if (isExpanded) {
                                toggleFolder(node.id);
                            } else if (parentItemId) {
                                focusTreeItem(parentItemId);
                            }
                        }
                    }}
                    style={styles.treeItem}
                >
                    <div
                        onMouseEnter={() => setHoveredItemId(itemId)}
                        onMouseLeave={() => setHoveredItemId(null)}
                        style={{
                            ...styles.menuItem,
                            paddingLeft: 10 + (depth * 20),
                            ...(hoveredItemId === itemId ? styles.menuItemHighlighted : {}),
                            ...(focusedItemId === itemId ? styles.menuItemFocused : {}),
                        }}
                    >
                        <span
                            aria-hidden='true'
                            style={{
                                ...styles.folderChevron,
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            }}
                        >
                            <svg width='14' height='14' viewBox='0 0 24 24' fill='none'>
                                <polyline
                                    points='9 6 15 12 9 18'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </span>
                        <span aria-hidden='true' style={styles.folderIcon}>
                            <svg width='17' height='17' viewBox='0 0 24 24' fill='none'>
                                <path
                                    d='M3 6.5A1.5 1.5 0 0 1 4.5 5h5l2 2h8A1.5 1.5 0 0 1 21 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5v-11Z'
                                    fill='currentColor'
                                />
                            </svg>
                        </span>
                        <span style={styles.itemLabel}>{node.name}</span>
                    </div>
                    {isExpanded && (
                        <div role='group'>
                            {renderTreeNodes(node.children, depth + 1, itemId)}
                        </div>
                    )}
                </div>
            );
        }

        const isSelected = node.provider.id === healthcareProviderId;
        const itemId = `provider:${node.provider.id}`;
        const providerLabel = getHealthcareProviderLabel(
            node.provider,
            clientCountByProviderId,
            licenseStatusByProviderId
        );
        const hasLicenseOverage = licenseStatusByProviderId[node.provider.id] === 'overage';
        return (
            <div
                key={itemId}
                ref={(element) => {
                    if (element) {
                        treeItemRefs.current.set(itemId, element);
                    } else {
                        treeItemRefs.current.delete(itemId);
                    }
                }}
                role='treeitem'
                data-tree-item-id={itemId}
                data-tree-label={node.provider.name}
                tabIndex={focusedItemId === itemId ? 0 : -1}
                aria-selected={isSelected}
                aria-current={isSelected ? 'true' : undefined}
                aria-level={depth + 1}
                aria-label={hasLicenseOverage ? `${providerLabel}. License allowance exceeded` : providerLabel}
                title={providerLabel}
                onFocus={(event) => {
                    if (event.target === event.currentTarget) {
                        setFocusedItemId(itemId);
                    }
                }}
                onClick={() => {
                    closeMenu(true);
                    if (!isSelected) {
                        onChange(node.provider.id);
                    }
                }}
                onKeyDown={(event) => {
                    // Activate providers and return to their parent folder using standard tree keys.
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        closeMenu(true);
                        if (!isSelected) {
                            onChange(node.provider.id);
                        }
                    } else if (event.key === 'ArrowLeft' && parentItemId) {
                        event.preventDefault();
                        focusTreeItem(parentItemId);
                    }
                }}
                onMouseEnter={() => setHoveredItemId(itemId)}
                onMouseLeave={() => setHoveredItemId(null)}
                style={{
                    ...styles.treeItem,
                    ...styles.menuItem,
                    ...styles.providerItem,
                    paddingLeft: 31 + (depth * 20),
                    ...(isSelected ? styles.menuItemSelected : {}),
                    ...(hoveredItemId === itemId ? styles.menuItemHighlighted : {}),
                    ...(focusedItemId === itemId ? styles.menuItemFocused : {}),
                }}
            >
                <span style={styles.itemLabel}>{providerLabel}</span>
                {isSelected && <span aria-hidden='true' style={styles.checkmark}>✓</span>}
            </div>
        );
    });

    const selectedLabel = selectedProvider
        ? getHealthcareProviderLabel(selectedProvider, clientCountByProviderId, licenseStatusByProviderId)
        : 'Select healthcare provider';
    const selectedHasLicenseOverage = !!selectedProvider &&
        licenseStatusByProviderId[selectedProvider.id] === 'overage';
    const menu = isOpen ? createPortal(
        <div
            ref={menuRef}
            role='tree'
            aria-label='Healthcare providers'
            onKeyDown={handleMenuKeyDown}
            onBlur={(event) => {
                // Close after focus leaves both the tree and its trigger.
                const nextFocusedElement = event.relatedTarget as Node | null;
                if (
                    nextFocusedElement &&
                    (menuRef.current?.contains(nextFocusedElement) || buttonRef.current?.contains(nextFocusedElement))
                ) {
                    return;
                }
                closeMenu();
            }}
            onClick={(event) => event.stopPropagation()}
            style={{
                ...styles.menu,
                top: menuPosition.top,
                right: menuPosition.right,
                width: menuPosition.width,
                maxHeight: menuPosition.maxHeight,
            }}
        >
            {providerTree.length > 0 ? renderTreeNodes(providerTree) : (
                <div style={styles.emptyState}>No healthcare providers available</div>
            )}
        </div>,
        document.body
    ) : null;

    return (
        <div ref={containerRef} style={{ ...styles.container, ...style }}>
            <button
                ref={buttonRef}
                type='button'
                aria-label={`Select healthcare provider. Current selection: ${selectedLabel}${
                    selectedHasLicenseOverage ? '. License allowance exceeded' : ''
                }`}
                aria-haspopup='tree'
                aria-expanded={isOpen}
                title={selectedLabel}
                onClick={() => {
                    if (isOpen) {
                        closeMenu();
                    } else {
                        openMenu();
                    }
                }}
                onKeyDown={(event) => {
                    if (!isOpen && event.key === 'ArrowDown') {
                        event.preventDefault();
                        openMenu();
                    }
                }}
                style={styles.button}
            >
                <span style={styles.buttonLabel}>{selectedLabel}</span>
                <span
                    aria-hidden='true'
                    style={{
                        ...styles.buttonChevron,
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                >
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                        <polyline
                            points='6 9 12 15 18 9'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </svg>
                </span>
            </button>
            {menu}
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        position: 'relative',
        display: 'inline-flex',
        flexShrink: 0,
    },
    button: {
        ...Theme.Fonts.body,
        width: '240px',
        minWidth: 0,
        height: '38px',
        padding: '7px 9px 7px 11px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        boxSizing: 'border-box',
    },
    buttonLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    buttonChevron: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'transform 160ms ease',
    },
    menu: {
        position: 'fixed',
        zIndex: 2200,
        overflowY: 'auto',
        padding: '6px',
        border: Theme.Colours.border,
        borderRadius: '8px',
        backgroundColor: Theme.Colours.workArea,
        boxShadow: '0 10px 28px rgba(0, 0, 0, 0.14)',
        boxSizing: 'border-box',
    },
    treeItem: {
        outline: 'none',
    },
    menuItem: {
        ...Theme.Fonts.body,
        appearance: 'none',
        WebkitAppearance: 'none',
        width: '100%',
        minHeight: '34px',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        paddingTop: '7px',
        paddingRight: '9px',
        paddingBottom: '7px',
        textAlign: 'left',
        boxSizing: 'border-box',
    },
    providerItem: {
        justifyContent: 'space-between',
    },
    menuItemHighlighted: {
        backgroundColor: 'rgba(129, 126, 228, 0.12)',
    },
    menuItemFocused: {
        boxShadow: 'inset 0 0 0 2px #5e5bc4',
    },
    menuItemSelected: {
        backgroundColor: 'rgba(129, 126, 228, 0.18)',
        fontWeight: 600,
    },
    folderChevron: {
        color: '#686868',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'transform 120ms ease',
    },
    folderIcon: {
        color: '#817ee4',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    itemLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    checkmark: {
        color: '#5e5bc4',
        fontWeight: 700,
        flexShrink: 0,
    },
    emptyState: {
        ...Theme.Fonts.body,
        color: '#686868',
        padding: '10px',
    },
};
